import { z } from 'zod'
import { oc } from '@orpc/contract'

/**
 * Creates the beHuman side of a Zocdoc booking: `Appointment` + `Encounter`, claiming a free
 * `Slot` on the mapped practitioner's schedule, and writing `ZOCDOC_APPOINTMENT_SYSTEM` so the two
 * are bound from the moment they exist.
 *
 * The counterpart of `zocdocLinkAppointment`: that one binds a visit somebody already booked in
 * beHuman by hand, this one creates the visit that was only ever booked on Zocdoc.
 *
 * **Only for a patient who is already linked.** An unlinked Zocdoc patient is refused, never
 * invited and never matched on name or birth date — the chart has to be confirmed by a human
 * first, through the details popup. Binding a real appointment (and later a real cancellation) to
 * a guessed chart is the mistake this refusal exists to prevent.
 *
 * **It takes only the Zocdoc appointment id.** Everything else — practitioner, chart, slot, visit
 * reason — is resolved server-side at the moment of the write, because a screen that computed
 * them rendered minutes ago.
 *
 * **No side effects.** Resources only: no patient email, no consent generation, no records pull.
 * Nothing reaches a patient as a consequence of an ops action; the patient already has Zocdoc's
 * own confirmation.
 */
export const zocdocImportAppointment = oc
  .route({ method: 'POST', path: '/v2/zocdoc/appointments/import' })
  .input(
    z.object({
      zocdocAppointmentId: z.string().min(1),
      /**
       * Publish a `free` Slot at the booking's instant when the practitioner has none, instead of
       * refusing with `no-slot`.
       *
       * Off by default, and deliberately explicit: it commits a practitioner to a time they never
       * offered. Never applied to `slot-claimed` — a second slot over a booked one double-books
       * the doctor — and never to `no-schedule`, where a Slot has nothing to hang off.
       */
      createSlot: z.boolean().optional(),
      /**
       * Create and invite a beHuman chart when the Zocdoc patient has none, instead of refusing
       * with `no-chart`.
       *
       * Off by default, and that default is the safety rule this endpoint was built on: binding a
       * real visit to a guessed chart puts one person's appointment on another person's record.
       * The console leaves it off — an operator can see the candidates and decide. The inbound
       * webhook turns it on, because the booking already exists on Zocdoc and there is nobody to
       * ask.
       *
       * It does NOT weaken the matching: a chart is matched on email **and** date of birth, two
       * matches refuse (`ambiguous-chart`), a proxy booking refuses (`proxy-booking`), and a chart
       * already bound to a different Zocdoc patient refuses rather than being re-pointed.
       */
      createPatient: z.boolean().optional(),
    }),
  )
  .output(
    z.object({
      status: z.enum([
        /** Appointment and Encounter created, slot claimed, identifier written. */
        'imported',
        /** A beHuman appointment already carries this Zocdoc id — the ids below are its. */
        'already-imported',
        /**
         * Zocdoc itself reports the booking cancelled, so there is nothing to import. A no-op
         * rather than a `refusal`: nobody has anything to fix, and the webhook runner must not
         * turn an out-of-order `created` delivery into a failed run.
         */
        'cancelled-at-source',
        /** Nothing was written. See `refusal`. */
        'refused',
      ]),
      refusal: z
        .object({
          reason: z.enum([
            /** Zocdoc gave no readable start, so there is no time to book. */
            'no-start',
            /** The visit has already happened; beHuman refuses to book into the past. */
            'past',
            /** The Zocdoc provider is not linked to a beHuman practitioner. */
            'provider-unmapped',
            /**
             * The linked practitioner's chart carries no `us-npi`. Zocdoc's provider API can only
             * be queried by NPI, so without one the booking's provider cannot be read at all —
             * fixed by adding the NPI to their record, not by re-mapping anything.
             */
            'practitioner-has-no-npi',
            /**
             * Zocdoc itself did not answer for that provider — it reports no such provider under
             * the practitioner's NPI, or the read failed. Distinct from `provider-unmapped`
             * because the beHuman side is correct and there is nothing for an operator to edit.
             */
            'zocdoc-unknown',
            /**
             * The Zocdoc patient has no confirmed beHuman chart. Invite and confirm them first —
             * deliberately a human step, unless the caller passed `createPatient`.
             */
            'no-chart',
            /**
             * Somebody booked on the patient's behalf, so the email on the booking may be the
             * booker's. Never auto-created: the invitation is a link into a chart, and sending it
             * to the wrong person is not a recoverable mistake. A person imports these.
             */
            'proxy-booking',
            /**
             * Zocdoc gave too little to create a chart from — the detail names which of first
             * name, last name, date of birth and email are missing. Email is required because a
             * chart that cannot be invited is a chart the patient can never reach.
             */
            'incomplete-patient',
            /**
             * Several beHuman charts match that email and date of birth. Picking one would be a
             * coin toss whose loser gets somebody else's visit on their record.
             */
            'ambiguous-chart',
            /**
             * The matching chart is already bound to a *different* Zocdoc patient record.
             * Re-pointing it discards a binding somebody confirmed, which is only ever a
             * deliberate act — "Replace the link" in the dashboard.
             */
            'chart-holds-other-zocdoc-patient',
            /**
             * The booking's practitioner belongs to no organization, or the lookup failed, so a
             * new chart would be filed under no owner. `meta.accounts` is what the practice access
             * rules read, so such a chart exists and is invisible: the run would report success,
             * the patient would be invited, and the practice would see neither.
             */
            'no-owner-organization',
            /** The practitioner has no slot at that time; publish availability first. */
            'no-slot',
            /** There is a slot, but it is already claimed by another booking. */
            'slot-claimed',
            /** The practitioner has no `Schedule` at all, so there is nothing to book onto. */
            'no-schedule',
          ]),
          detail: z.string(),
        })
        .optional(),
      appointmentId: z.string().optional(),
      /** What a cancellation acts on, so a caller never has to look it up again. */
      encounterId: z.string().optional(),
      slotId: z.string().optional(),
      /** A Slot was published for this import because `createSlot` was ticked. */
      slotCreated: z.boolean().optional(),
      /**
       * What `createPatient` did, when it did anything. Absent on the ordinary path where the
       * chart was already linked.
       *
       * `invited: false` on a `created` chart is not a failure of the import — the chart exists,
       * carries the Zocdoc identifier and holds the visit; only the patient's invitation did not
       * go out, and `inviteError` says why. It can be re-sent from the dashboard.
       */
      patientChart: z
        .object({
          outcome: z.enum(['created', 'linked']),
          patientId: z.string(),
          invited: z.boolean().optional(),
          inviteError: z.string().optional(),
          detail: z.string().optional(),
        })
        .optional(),
      /**
       * What became of the insurance the booking carried. It is filed as an insurance request for
       * staff review — never a Coverage — and `failed` does not fail the import: the visit is on
       * the calendar either way.
       */
      insuranceDraft: z
        .object({
          status: z.enum(['saved', 'already-saved', 'self-pay', 'none', 'failed']),
          payerName: z.string().optional(),
          message: z.string().optional(),
        })
        .optional(),
      /**
       * Zocdoc's end time disagreed with the claimed slot's. The **slot wins**: the Encounter's
       * period and the calendar grid have to match the slot that was actually claimed. Said out
       * loud so the difference is not discovered later in a chart.
       */
      endMismatch: z.string().optional(),
    }),
  )
